import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
  getMetaMatchParams,
  trackPurchase,
  trackPurchaseFailed,
} from '../../utils/analytics';
// 📊 자체 이벤트 트래킹 (Firestore)
import { trackPaymentComplete, trackPaymentFailed } from '../../utils/eventTracker';
import {
  createPurchaseEventId,
  trackMetaPurchasePixel,
} from '../../utils/paymentTracking';
import PaymentFlowStatus from '../../components/PaymentFlowStatus';
import { resolvePaymentCouponCode } from '../../utils/paymentPromo';

const PaymentWidgetSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const hasProcessedRef = useRef(false);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);

  // API 기본 URL 설정
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const confirmPayment = async () => {
      if (hasProcessedRef.current || processingRef.current) return;
      processingRef.current = true;

      try {
        // URL 파라미터에서 결제 정보 추출
        const paymentKey = searchParams.get('paymentKey');
        const orderId = searchParams.get('orderId');
        const amount = searchParams.get('amount');
        const purchaseEventId = createPurchaseEventId(orderId);
        // 서버사이드 금액 검증용 (ProSubscription 일반결제 플로우에서 전달)
        const planIdParam = searchParams.get('planId');
        const promoParam = searchParams.get('promo');
        const couponParam = searchParams.get('coupon');

        const finalCouponCode = resolvePaymentCouponCode(
          couponParam,
          promoParam,
        );

        if (!paymentKey || !orderId || !amount) {
          throw new Error('결제 정보가 올바르지 않습니다.');
        }

        if (!currentUser) {
          throw new Error('로그인이 필요합니다.');
        }

        const API_BASE_URL = getApiBaseUrl();
        const idToken = await currentUser.getIdToken(true);

        const confirmPayload = {
          payment_key: paymentKey,
          order_id: orderId,
          amount: parseInt(amount),
          event_id: purchaseEventId,
          ...getMetaMatchParams(),
        };
        if (planIdParam) confirmPayload.plan_id = planIdParam;
        if (finalCouponCode) confirmPayload.coupon_code = finalCouponCode;

        // 백엔드에 결제 승인 요청 (V2 파라미터 포함)
        const response = await fetch(`${API_BASE_URL}/api/payment/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(confirmPayload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || errorData.error || '결제 승인에 실패했습니다.');
        }

        const result = await response.json();

        if (result.success) {
          hasProcessedRef.current = true;
          // 구독 상태 업데이트 (캐시 무시)
          await fetchSubscriptionStatus(true);
          
          // 🔥 백엔드에서 실제 플랜 정보를 받아서 사용
          const actualPlanId = result.subscription?.plan_id || result.plan_id || 'unknown';
          
          const actualAmount = result.subscription?.amount || result.amount || parseInt(amount);
          
          // GA4 결제 완료 이벤트 전송
          trackPurchase(orderId, actualPlanId, actualAmount);
          
          // 📊 자체 결제 완료 이벤트 트래킹 (Firestore)
          const promoCode = searchParams.get('promo') || searchParams.get('coupon') || null;
          trackPaymentComplete(orderId, actualPlanId, actualAmount, promoCode, null);
          trackMetaPurchasePixel({
            amount: actualAmount,
            eventId: result.meta_event_id || purchaseEventId,
            orderId,
            planId: actualPlanId,
          });
          
          // 성공 페이지로 이동 - PaymentSuccess 컴포넌트가 기대하는 데이터 구조로 전달
          navigate('/payment/success', {
            state: {
              orderId: orderId,
              orderNo: orderId, // PaymentSuccess에서 사용하는 필드명
              amount: parseInt(amount),
              paymentKey: paymentKey,
              planId: actualPlanId, // 백엔드에서 받은 실제 플랜 정보
              planName: result.subscription?.plan_name || result.plan_name, // 플랜 이름도 전달
              success: true
            }
          });
        } else {
          throw new Error(result.error || '결제 처리에 실패했습니다.');
        }
      } catch (error) {
        console.error('결제 확인 중 오류:', error);
        
        // GA4 결제 실패 이벤트 전송
        const amount = searchParams.get('amount');
        trackPurchaseFailed(error.message, 'unknown', parseInt(amount) || 0);
        
        // 📊 자체 결제 실패 이벤트 트래킹 (Firestore)
        trackPaymentFailed('unknown', parseInt(amount) || 0, 'CONFIRM_FAILED', error.message);
        
        if (mountedRef.current) {
          setError(error.message);
          setIsProcessing(false);
        }
      } finally {
        processingRef.current = false;
      }
    };

    confirmPayment();
    return () => {
      mountedRef.current = false;
    };
  }, [searchParams, currentUser, navigate, fetchSubscriptionStatus]);

  const isPaymentOutcomePending = Boolean(
    error
    && (
      error.includes('결제 결과를 확인')
      || error.includes('다시 결제하지')
      || error.includes('이전 결제 결과')
    )
  );

  if (isProcessing) {
    return (
      <PaymentFlowStatus
        variant="processing"
        title="결제를 확인하고 있어요"
        description="결제 승인과 PRO 활성화를 진행 중이에요. 이 화면을 닫지 말아주세요."
      />
    );
  }

  if (error) {
    return (
      <PaymentFlowStatus
        variant={isPaymentOutcomePending ? 'pending' : 'error'}
        title={isPaymentOutcomePending ? '결제 결과를 확인하고 있어요' : '결제를 완료하지 못했어요'}
        description={
          isPaymentOutcomePending
            ? error
            : '결제 승인을 완료하지 못했어요. 잠시 후 다시 시도해주세요.'
        }
        detailCode={!isPaymentOutcomePending ? error : undefined}
        primaryAction={{
          label: isPaymentOutcomePending ? '결제 내역 확인하기' : '결제 다시 시도하기',
          onClick: () => navigate(
            isPaymentOutcomePending ? '/subscription' : '/subscription/pro'
          ),
        }}
      />
    );
  }

  return null;
};

export default PaymentWidgetSuccess;